// HELP.A2b (#2055, ADR-003 §10.3) — the one entry point this story adds.
//
// PLACEMENT, AND WHY (recon flagged this explicitly as needing a documented
// answer): no cross-phase panel slot exists anywhere in apps/web/src, and no
// A5 Help panel exists to mount into (recon finding #5). Rather than bolt a
// new prop onto Sidebar — shared, high-blast-radius, mounted on both
// ProjectLayout and Dashboard — this mounts as a self-contained floating
// entry point from the two project layouts, ProjectLayout.tsx and the
// chat-only ProjectChatLayout.tsx, which both resolve `projectId` from the
// route for every phase under a project (see those files). It is
// deliberately reachable from every phase within a project, which is most of
// ADR-003's "every phase surface" requirement.
//
// NOT SOLVED HERE: dashboard-level invocation with no active project.
// buildSituationEnvelope requires a projectId (services/situationEnvelope.ts
// — construction aborts without one), so a project-less capture is not a
// smaller version of this feature, it is a different one (what would the
// envelope even contain?). That is a real, undecided product question, not
// an oversight, and is out of this story's scope — reported rather than
// silently narrowed.
//
// Collapsible, not a modal: no focus-trap/dialog machinery, which keeps this
// proportionate to "capture-only, deliberately minimal" (issue #2055's own
// framing). Keyboard-operable via aria-expanded/aria-controls on a plain
// button, which is the same pattern lucide-react-icon toggle buttons already
// use elsewhere in this codebase (e.g. HelpPopover.tsx — an unrelated,
// pre-existing "Help" feature; see that component's own header on why the
// name collision matters and this is NOT it).

import { useId, useState } from 'react';
import HelpCaptureForm from './HelpCaptureForm';

interface Props {
  projectId: string;
}

export default function HelpCaptureEntry({ projectId }: Props) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        // Chat overlap fix — ChatPage publishes the height of its bottom
        // stack (composer / archived footer / dev-agent Continue-Skip row) as
        // --chat-bottom-stack-height on :root, so on chat this button sits
        // ABOVE the send button instead of on it.
        //
        // On chat it also clears ChatHistory's "↓ New messages"
        // (jump-to-latest): sticky at bottom 12, ~28px tall, right-aligned, so
        // it spans 12-40px above the stack. The bounded chat layout is what
        // made that control reachable, in this corner, so the clearance is
        // taken here: 48px = 40px + an 8px gap.
        //
        // No other page sets the variable. The -28px fallback resolves to
        // 48px - 28px = 20px, the button's original bottom everywhere else.
        bottom: 'calc(48px + var(--chat-bottom-stack-height, -28px))',
        zIndex: 40,
        // Right-aligned column: the button stays pinned to the right edge
        // when the 420px form opens above it (previously it was an inline
        // child and jumped to the container's left edge).
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        // The container box is as wide as the open form, and its empty area
        // beside the button used to swallow clicks meant for whatever is
        // underneath (the composer's send button). Only the panel and the
        // button take pointer events.
        pointerEvents: 'none',
      }}
    >
      {/* R2 review — the id target must exist in the DOM whenever
          aria-controls references it, or the closed state has an invalid
          ARIA reference. The container stays mounted; only its contents are
          conditional, and `hidden` removes it from the accessibility tree
          and tab order while closed (same effect as unmounting, without the
          dangling id reference). */}
      <div id={panelId} style={{ marginBottom: 8, pointerEvents: 'auto' }} hidden={!open}>
        {open && <HelpCaptureForm projectId={projectId} />}
      </div>
      <button
        type="button"
        data-testid="help-capture-entry"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? 'Close question form' : 'Ask a quick question'}
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'var(--primary)',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '999px',
          width: 44,
          height: 44,
          fontSize: 18,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          pointerEvents: 'auto',
        }}
      >
        {open ? '✕' : '?'}
      </button>
    </div>
  );
}
